import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Alert, Chip, Tooltip } from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Supercluster from 'supercluster';
import { fetchDevices, fetchDevicePictures } from '../services/apiService';
import { isValidCoordinate } from '../utils/reportFormatters';
import {
  WalteroCard,
  WalteroTypography,
  WalteroButton,
  WalteroTabs,
  WalteroTabPanel
} from './waltero';
import ImageModal from './shared/ImageModal';
import './VisionTab.css';

const VIEW_MODES = [
  { id: 'map', label: 'Map View', description: 'Explore captures by location' },
  { id: 'gallery', label: 'Gallery', description: 'Latest frame from each sensor' },
  { id: 'timeline', label: 'Timeline', description: 'Scroll through capture history' }
];

const MAX_IMAGES_PER_SENSOR = 40;

const MapEventBridge = ({ onChange }) => {
  useMapEvents({
    moveend: (event) => {
      const map = event.target;
      onChange(map.getBounds(), map.getZoom());
    },
    zoomend: (event) => {
      const map = event.target;
      onChange(map.getBounds(), map.getZoom());
    }
  });

  return null;
};

MapEventBridge.propTypes = {
  onChange: PropTypes.func.isRequired
};

const getTimestamp = (picture) => {
  if (!picture) return null;

  const numericCandidates = [
    picture.createdTimestamp,
    picture.created_timestamp,
    picture.uploadedTimestamp,
    picture.uploaded_timestamp
  ].filter(value => typeof value === 'number' && !Number.isNaN(value));

  for (const numericValue of numericCandidates) {
    const milliseconds = numericValue > 1e12 ? numericValue : numericValue * 1000;
    const date = new Date(milliseconds);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }
  }

  const stringCandidates = [
    picture.lastModified,
    picture.last_modified,
    picture.timestamp,
    picture.createdAt,
    picture.capturedAt,
    picture.captured_at,
    picture.recordedAt
  ];

  for (const candidate of stringCandidates) {
    if (!candidate) continue;
    const parsed = new Date(candidate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  const filename = picture.filename || picture.fileName || picture.key || '';
  if (filename) {
    const match = filename.match(/(\d{4})(\d{2})(\d{2})T?(\d{2})(\d{2})(\d{2})/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      const date = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      );
      if (!Number.isNaN(date.getTime())) {
        return date;
      }
    }
  }

  return null;
};

const getDateKey = (picture) => {
  const timestamp = getTimestamp(picture);
  if (!timestamp) return 'Unknown';
  return timestamp.toISOString().split('T')[0];
};

const formatDateTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const formatTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const getPictureKey = (picture) => {
  if (!picture) return null;
  return picture.key || picture.url || picture.imageUrl || picture.filename || picture.id || null;
};

const sortPicturesByNewest = (pictures) => {
  if (!Array.isArray(pictures)) return [];
  return pictures.slice().sort((a, b) => {
    const timeA = getTimestamp(a)?.getTime() ?? 0;
    const timeB = getTimestamp(b)?.getTime() ?? 0;
    return timeB - timeA;
  });
};

const groupPicturesByDate = (pictures) => {
  const grouped = {};
  pictures.forEach((picture) => {
    const dateKey = getDateKey(picture);
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(picture);
  });
  return grouped;
};

const VisionTab = ({ selectedOrgId, startDate, endDate, refreshKey }) => {
  const [activeMode, setActiveMode] = useState(VIEW_MODES[0].id);
  const [devices, setDevices] = useState([]);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceError, setDeviceError] = useState(null);

  const [imageCache, setImageCache] = useState({});
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalPictures, setModalPictures] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalIndex, setModalIndex] = useState(0);
  const [mapBounds, setMapBounds] = useState(null);
  const [mapZoom, setMapZoom] = useState(5);
  const mapRef = useRef(null);

  // Reset state when org changes
  useEffect(() => {
    setDevices([]);
    setImageCache({});
    setActiveMode(VIEW_MODES[0].id);
    setDeviceError(null);
    setImageError(null);
  }, [selectedOrgId]);

  // Load devices and pictures when refresh key bumps
  useEffect(() => {
    if (!selectedOrgId || !startDate || !endDate) {
      return;
    }

    if (!refreshKey) {
      return;
    }

    let isActive = true;

    const loadData = async () => {
      setDeviceLoading(true);
      setImageLoading(true);
      setDeviceError(null);
      setImageError(null);
      setDevices([]);
      setImageCache({});

      try {
        const response = await fetchDevices(selectedOrgId, { limit: 1000 });
        if (!isActive) return;

        const rawDevices = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.devices)
            ? response.devices
            : Array.isArray(response)
              ? response
              : [];

        setDevices(rawDevices);

        if (rawDevices.length === 0) {
          setImageCache({});
          setImageError('No sensors found with camera access in this organization.');
          setImageLoading(false);
          return;
        }

        const devicesToLoad = rawDevices;
        const pictureResults = await Promise.allSettled(
          devicesToLoad.map(device =>
            fetchDevicePictures(device.id, {
              startDate,
              endDate,
              limit: MAX_IMAGES_PER_SENSOR
            })
          )
        );

        if (!isActive) return;

        const newCache = {};
        let sensorsWithImages = 0;
        let totalPictures = 0;

        for (let index = 0; index < devicesToLoad.length; index += 1) {
          const device = devicesToLoad[index];
          const result = pictureResults[index];

          if (!device || !result) {
            continue;
          }

          if (result.status === 'fulfilled') {
            let response = result.value;
            const pageSize = MAX_IMAGES_PER_SENSOR;

            const extractTotalCount = (payload) => {
              if (!payload) return 0;
              if (typeof payload.totalCount === 'number') return payload.totalCount;
              const pageInfo = payload.pageInfo || {};
              if (typeof pageInfo.totalItems === 'number') return pageInfo.totalItems;
              if (typeof pageInfo.totalCount === 'number') return pageInfo.totalCount;
              if (Array.isArray(payload.pictures)) return payload.pictures.length;
              return 0;
            };

            const inferStartIndex = (payload, fallbackValue = 0) => {
              const pageInfo = payload?.pageInfo || {};
              if (typeof pageInfo.startIndex === 'number') return pageInfo.startIndex;
              if (typeof pageInfo.start_index === 'number') return pageInfo.start_index;
              if (typeof pageInfo.currentPage === 'number') {
                return Math.max((pageInfo.currentPage - 1) * pageSize, 0);
              }
              if (typeof pageInfo.currentPage === 'string') {
                const parsed = parseInt(pageInfo.currentPage, 10);
                if (!Number.isNaN(parsed)) {
                  return Math.max((parsed - 1) * pageSize, 0);
                }
              }
              return fallbackValue;
            };

            let totalCount = extractTotalCount(response);
            let startIndexUsed = inferStartIndex(response, 0);
            const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 1;

            if (totalPages > 1 && startIndexUsed === 0) {
              const lastStartIndex = Math.max(totalCount - pageSize, 0);
              if (lastStartIndex > 0) {
                try {
                  const latestPage = await fetchDevicePictures(device.id, {
                    startDate,
                    endDate,
                    limit: pageSize,
                    nextToken: lastStartIndex.toString()
                  });
                  if (latestPage?.pictures?.length) {
                    response = latestPage;
                    totalCount = extractTotalCount(latestPage) || totalCount;
                    startIndexUsed = inferStartIndex(latestPage, lastStartIndex) || lastStartIndex;
                  }
                } catch (latestError) {
                  console.error('Failed to fetch latest page for device', device.id, latestError);
                }
              }
            }

            const pictures = Array.isArray(response?.pictures)
              ? sortPicturesByNewest(response.pictures)
              : [];
            const groupedByDate = groupPicturesByDate(pictures);
            const pageInfo = response?.pageInfo || {};
            const nextToken = pageInfo.nextToken || pageInfo.next_token || response?.nextToken || response?.next_token || null;
            const hasNextPage = typeof pageInfo.hasNextPage === 'boolean' ? pageInfo.hasNextPage : Boolean(nextToken);
            const hasMoreOlder = startIndexUsed > 0;

            if (pictures.length > 0) {
              sensorsWithImages += 1;
              totalPictures += pictures.length;
            }

            newCache[device.id] = {
              pictures,
              groupedByDate,
              latest: pictures[0] || null,
              nextToken,
              hasMore: hasMoreOlder,
              isFetchingMore: false,
              loadMoreError: null,
              error: null,
              oldestStartIndex: startIndexUsed,
              loadedStartIndices: [startIndexUsed],
              pageSize,
              totalCount,
              hasNextServerPage: hasNextPage
            };
          } else {
            newCache[device.id] = {
              pictures: [],
              groupedByDate: {},
              latest: null,
              nextToken: null,
              hasMore: false,
              isFetchingMore: false,
              loadMoreError: result.reason?.message || 'Failed to load images',
              error: result.reason?.message || 'Failed to load images',
              oldestStartIndex: 0,
              loadedStartIndices: [],
              pageSize: MAX_IMAGES_PER_SENSOR,
              totalCount: 0,
              hasNextServerPage: false
            };
          }
        }

        setImageCache(newCache);

        if (sensorsWithImages === 0) {
          setImageError('No images were found for the selected time range.');
        }

        console.log('📸 Vision cache prepared', {
          sensorsWithImages,
          totalPictures,
          deviceCount: devicesToLoad.length
        });
      } catch (error) {
        if (!isActive) return;
        console.error('❌ Failed to load Vision data', error);
        setDeviceError(error.message || 'Failed to load sensors');
        setImageCache({});
      } finally {
        if (isActive) {
          setDeviceLoading(false);
          setImageLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isActive = false;
    };
  }, [selectedOrgId, startDate, endDate, refreshKey]);

  const handleOpenModal = useCallback((deviceId, startingIndex = 0) => {
    const cacheEntry = imageCache[deviceId];
    if (!cacheEntry || cacheEntry.pictures.length === 0) return;

    const index = Math.max(0, Math.min(startingIndex, cacheEntry.pictures.length - 1));

    setModalPictures(cacheEntry.pictures);
    setSelectedImage(cacheEntry.pictures[index]);
    setModalIndex(index);
    setModalOpen(true);
  }, [imageCache]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setModalPictures([]);
    setSelectedImage(null);
    setModalIndex(0);
  }, []);

  const handleNextModalImage = useCallback(() => {
    if (modalPictures.length <= 1) return;
    const nextIndex = (modalIndex + 1) % modalPictures.length;
    setModalIndex(nextIndex);
    setSelectedImage(modalPictures[nextIndex]);
  }, [modalPictures, modalIndex]);

  const handlePrevModalImage = useCallback(() => {
    if (modalPictures.length <= 1) return;
    const prevIndex = modalIndex === 0 ? modalPictures.length - 1 : modalIndex - 1;
    setModalIndex(prevIndex);
    setSelectedImage(modalPictures[prevIndex]);
  }, [modalPictures, modalIndex]);

  const handleMapStateChange = useCallback((bounds, zoom) => {
    setMapBounds(bounds);
    setMapZoom(zoom);
  }, []);

const loadMorePicturesForDevice = useCallback(async (deviceId) => {
    const entry = imageCache[deviceId];
    if (!entry || entry.isFetchingMore) {
      return;
    }

    const pageSize = entry.pageSize ?? MAX_IMAGES_PER_SENSOR;
    const currentStart = entry.oldestStartIndex ?? 0;

    if (currentStart <= 0) {
      if (entry.hasMore) {
        setImageCache(prev => {
          const current = prev[deviceId];
          if (!current) return prev;
          return {
            ...prev,
            [deviceId]: {
              ...current,
              hasMore: false
            }
          };
        });
      }
      return;
    }

    const nextStartIndex = Math.max(currentStart - pageSize, 0);
    const loadedIndices = new Set(entry.loadedStartIndices || []);

    if (loadedIndices.has(nextStartIndex)) {
      if (nextStartIndex === 0) {
        setImageCache(prev => {
          const current = prev[deviceId];
          if (!current) return prev;
          return {
            ...prev,
            [deviceId]: {
              ...current,
              hasMore: false
            }
          };
        });
      }
      return;
    }

    setImageCache(prev => {
      const current = prev[deviceId];
      if (!current) return prev;
      return {
        ...prev,
        [deviceId]: {
          ...current,
          isFetchingMore: true,
          loadMoreError: null,
          pendingStartIndex: nextStartIndex
        }
      };
    });

    try {
      const response = await fetchDevicePictures(deviceId, {
        startDate,
        endDate,
        limit: pageSize,
        nextToken: nextStartIndex.toString()
      });

      const incomingPictures = Array.isArray(response?.pictures) ? response.pictures : [];
      const sortedIncoming = sortPicturesByNewest(incomingPictures);
      const pageInfo = response?.pageInfo || {};
      const totalCount = typeof response?.totalCount === 'number'
        ? response.totalCount
        : entry.totalCount;
      const hasMoreOlder = nextStartIndex > 0;

      setImageCache(prev => {
        const current = prev[deviceId];
        if (!current) return prev;

        const existingPictures = current.pictures || [];
        const mergedList = [...existingPictures, ...sortedIncoming];
        const mergedMap = new Map();
        mergedList.forEach((picture, index) => {
          const key = getPictureKey(picture) || `${index}-${JSON.stringify(picture)}`;
          if (!mergedMap.has(key)) {
            mergedMap.set(key, picture);
          }
        });

        const mergedPictures = sortPicturesByNewest(Array.from(mergedMap.values()));
        const groupedByDate = groupPicturesByDate(mergedPictures);
        const updatedLoaded = Array.from(new Set([...(current.loadedStartIndices || []), nextStartIndex]))
          .sort((a, b) => b - a);

        return {
          ...prev,
          [deviceId]: {
            ...current,
            pictures: mergedPictures,
            groupedByDate,
            latest: mergedPictures[0] || current.latest,
            oldestStartIndex: nextStartIndex,
            loadedStartIndices: updatedLoaded,
            totalCount,
            hasMore: hasMoreOlder,
            isFetchingMore: false,
            loadMoreError: null,
            error: null,
            hasNextServerPage: typeof pageInfo.hasNextPage === 'boolean' ? pageInfo.hasNextPage : current.hasNextServerPage
          }
        };
      });
    } catch (error) {
      console.error('Failed to load more pictures for device', deviceId, error);
      setImageCache(prev => {
        const current = prev[deviceId];
        if (!current) return prev;
        return {
          ...prev,
          [deviceId]: {
            ...current,
            isFetchingMore: false,
            loadMoreError: error?.message || 'Failed to load more images',
            error: error?.message || current.error || 'Failed to load more images'
          }
        };
      });
    }
  }, [imageCache, startDate, endDate]);

  const handleTimelineScroll = useCallback((deviceId, event) => {
    const entry = imageCache[deviceId];
    if (!entry || !entry.hasMore || entry.isFetchingMore) {
      return;
    }

    const { scrollLeft, clientWidth, scrollWidth } = event.currentTarget;
    const remaining = scrollWidth - (scrollLeft + clientWidth);
    if (remaining < 150) {
      loadMorePicturesForDevice(deviceId);
    }
  }, [imageCache, loadMorePicturesForDevice]);

  const buildPhotoIcon = useCallback(({ imageUrl, isCluster, count }) => {
    const hasImage = Boolean(imageUrl);
    const classes = ['vision-photo-icon'];

    if (isCluster) {
      classes.push('vision-photo-icon--cluster');
    }

    if (!hasImage) {
      classes.push('vision-photo-icon--empty');
    }

    const safeUrl = hasImage ? imageUrl : '';

    const html = ReactDOMServer.renderToStaticMarkup(
      <div className={classes.join(' ')}>
        {hasImage ? (
          <img src={safeUrl} alt="" className="vision-photo-icon__img" />
        ) : (
          <div className="vision-photo-icon__placeholder" />
        )}
        {isCluster && Number(count) > 1 && (
          <span className="vision-photo-count">{count}</span>
        )}
      </div>
    );
    const size = isCluster ? 64 : 58;

    return L.divIcon({
      html,
      className: 'vision-photo-icon-wrapper',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2]
    });
  }, []);

  const devicesWithImages = useMemo(() => {
    return devices.filter(device => (imageCache[device.id]?.pictures?.length || 0) > 0);
  }, [devices, imageCache]);

  const allPictures = useMemo(() => {
    return Object.values(imageCache).flatMap(entry => entry.pictures || []);
  }, [imageCache]);

  const captureWindow = useMemo(() => {
    if (allPictures.length === 0) {
      return { first: null, last: null };
    }

    const timestamps = allPictures
      .map(getTimestamp)
      .filter((date) => date instanceof Date && !Number.isNaN(date.getTime()));

    if (timestamps.length === 0) {
      return { first: null, last: null };
    }

    timestamps.sort((a, b) => a.getTime() - b.getTime());
    return {
      first: timestamps[0],
      last: timestamps[timestamps.length - 1]
    };
  }, [allPictures]);

  const devicesWithCoordinates = useMemo(() => {
    return devices.filter(device => isValidCoordinate(device.latitude, device.longitude));
  }, [devices]);

  const deviceBounds = useMemo(() => {
    if (devicesWithCoordinates.length === 0) return null;
    const latLngs = devicesWithCoordinates
      .filter(device => isValidCoordinate(device.latitude, device.longitude))
      .map(device => [device.latitude, device.longitude]);

    if (latLngs.length === 0) {
      return null;
    }

    return L.latLngBounds(latLngs);
  }, [devicesWithCoordinates]);

  const photoPoints = useMemo(() => {
    return devicesWithCoordinates
      .map(device => {
        const cacheEntry = imageCache[device.id];
        const latestImage = cacheEntry?.latest;
        const timestamp = getTimestamp(latestImage);

        return {
          type: 'Feature',
          properties: {
            deviceId: device.id,
            serialNumber: device.serialnumber || device.deviceName || device.id,
            deviceName: device.name || device.serialnumber || 'Sensor',
            latestImageUrl: latestImage?.url || null,
            latestTimestamp: timestamp ? timestamp.getTime() : 0,
            latitude: device.latitude,
            longitude: device.longitude
          },
          geometry: {
            type: 'Point',
            coordinates: [device.longitude, device.latitude]
          }
        };
      })
      .filter(feature => Number.isFinite(feature.geometry.coordinates[0]) && Number.isFinite(feature.geometry.coordinates[1]));
  }, [devicesWithCoordinates, imageCache]);

  const clusterIndex = useMemo(() => {
    if (photoPoints.length === 0) return null;

    const index = new Supercluster({
      radius: 80,
      maxZoom: 18,
      map: (properties) => ({
        deviceId: properties.deviceId,
        serialNumber: properties.serialNumber,
        deviceName: properties.deviceName,
        latestImageUrl: properties.latestImageUrl,
        latestTimestamp: properties.latestTimestamp
      }),
      reduce: (accumulated, properties) => {
        if (
          properties.latestTimestamp > (accumulated.latestTimestamp ?? -Infinity)
        ) {
          accumulated.latestImageUrl = properties.latestImageUrl;
          accumulated.latestTimestamp = properties.latestTimestamp;
          accumulated.deviceId = properties.deviceId;
          accumulated.serialNumber = properties.serialNumber;
          accumulated.deviceName = properties.deviceName;
        }
      }
    });

    index.load(photoPoints);
    return index;
  }, [photoPoints]);

  const clusters = useMemo(() => {
    if (!clusterIndex) return [];

    const boundsToUse = mapBounds || deviceBounds;
    if (!boundsToUse) {
      return clusterIndex.getClusters([-180, -85, 180, 85], Math.round(mapZoom));
    }

    const bbox = [
      boundsToUse.getWest(),
      boundsToUse.getSouth(),
      boundsToUse.getEast(),
      boundsToUse.getNorth()
    ];

    return clusterIndex.getClusters(bbox, Math.round(mapZoom));
  }, [clusterIndex, mapBounds, deviceBounds, mapZoom]);

  const defaultMapCenter = useMemo(() => {
    if (deviceBounds && deviceBounds.isValid()) {
      const center = deviceBounds.getCenter();
      return [center.lat, center.lng];
    }

    if (devicesWithCoordinates.length > 0) {
      return [devicesWithCoordinates[0].latitude, devicesWithCoordinates[0].longitude];
    }

    return [0, 0];
  }, [deviceBounds, devicesWithCoordinates]);

  const handleMapReady = useCallback((mapInstance) => {
    mapRef.current = mapInstance;

    if (deviceBounds && deviceBounds.isValid()) {
      mapInstance.fitBounds(deviceBounds, { padding: [40, 40] });
    } else {
      mapInstance.setView(defaultMapCenter, mapInstance.getZoom(), { animate: false });
    }

    const currentBounds = mapInstance.getBounds();
    const currentZoom = mapInstance.getZoom();
    handleMapStateChange(currentBounds, currentZoom);
  }, [deviceBounds, defaultMapCenter, handleMapStateChange]);

  const renderLoadingState = () => (
    <WalteroCard className="vision-loading-card">
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={32} />
        <WalteroTypography variant="body1" color="secondary">
          Preparing image feeds...
        </WalteroTypography>
      </Box>
    </WalteroCard>
  );

  const renderEmptyState = () => (
    <WalteroCard className="vision-empty-card">
      <WalteroTypography variant="h6" color="secondary" gutterBottom>
        No image activity discovered yet
      </WalteroTypography>
      <WalteroTypography variant="body2" color="textSecondary">
        Adjust the date range or request fresh captures to populate this view.
      </WalteroTypography>
    </WalteroCard>
  );

  const renderMapView = () => {
    if (devicesWithCoordinates.length === 0) {
      return (
        <WalteroCard className="vision-empty-card">
          <WalteroTypography variant="h6" color="secondary" gutterBottom>
            No mapped sensors available
          </WalteroTypography>
          <WalteroTypography variant="body2" color="textSecondary">
            Add coordinates to sensors to unlock the map view.
          </WalteroTypography>
        </WalteroCard>
      );
    }

    return (
      <WalteroCard className="vision-map-card">
        <div className="vision-map-toolbar">
          <div>
            <WalteroTypography variant="h6" component="h3">
              Map View
            </WalteroTypography>
            <WalteroTypography variant="body2" color="textSecondary">
              Tap a sensor to preview captures and open the gallery.
            </WalteroTypography>
          </div>
          <Chip label={`${devicesWithCoordinates.length} mapped sensors`} size="small" color="primary" />
        </div>
        <div className="vision-map-container">
          <MapContainer
            center={defaultMapCenter}
            zoom={Math.round(mapZoom)}
            minZoom={2}
            maxZoom={18}
            style={{ height: '100%', width: '100%' }}
            preferCanvas={true}
            whenCreated={handleMapReady}
            bounds={deviceBounds && deviceBounds.isValid() ? deviceBounds : undefined}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapEventBridge onChange={handleMapStateChange} />

            {clusterIndex && clusters.map(cluster => {
              const [longitude, latitude] = cluster.geometry.coordinates;
              const isCluster = Boolean(cluster.properties.cluster);

              if (isCluster) {
                const rawClusterId = cluster.id ?? cluster.properties.cluster_id;
                const clusterId = typeof rawClusterId === 'number'
                  ? rawClusterId
                  : Number(rawClusterId);

                if (!Number.isFinite(clusterId)) {
                  return null;
                }

                const icon = buildPhotoIcon({
                  imageUrl: cluster.properties.latestImageUrl,
                  isCluster: true,
                  count: cluster.properties.point_count
                });

                return (
                  <Marker
                    key={`cluster-${clusterId}`}
                    position={[latitude, longitude]}
                    icon={icon}
                    eventHandlers={{
                      click: () => {
                        if (!mapRef.current) return;

                        const leaves = clusterIndex.getLeaves(clusterId, Infinity);
                        const leafLatLngs = leaves
                          .map(leaf => {
                            const [lng, lat] = leaf.geometry.coordinates;
                            return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
                          })
                          .filter(Boolean);

                        if (leafLatLngs.length > 1) {
                          const bounds = L.latLngBounds(leafLatLngs);
                          mapRef.current.fitBounds(bounds, { padding: [60, 60], animate: true, duration: 0.45 });
                        } else {
                          const expansionZoom = clusterIndex.getClusterExpansionZoom(clusterId);
                          const maxZoom = typeof mapRef.current.getMaxZoom === 'function'
                            ? mapRef.current.getMaxZoom()
                            : 18;
                          const targetZoom = Math.min(expansionZoom + 1, maxZoom || 18);
                          const targetLatLng = L.latLng(latitude, longitude);
                          mapRef.current.flyTo(targetLatLng, targetZoom, { duration: 0.45, animate: true });
                        }

                        const updateMapState = () => {
                          if (!mapRef.current) return;
                          const bounds = mapRef.current.getBounds();
                          const zoom = mapRef.current.getZoom();
                          handleMapStateChange(bounds, zoom);
                          mapRef.current.off('moveend', updateMapState);
                        };

                        mapRef.current.on('moveend', updateMapState);
                        setTimeout(updateMapState, 600);
                      }
                    }}
                  />
                );
              }

              const icon = buildPhotoIcon({
                imageUrl: cluster.properties.latestImageUrl,
                isCluster: false,
                count: 1
              });

              const deviceId = cluster.properties.deviceId || cluster.properties.serialNumber;

              if (!deviceId) {
                return null;
              }

              return (
                <Marker
                  key={`device-${deviceId}`}
                  position={[latitude, longitude]}
                  icon={icon}
                  eventHandlers={{
                    click: () => handleOpenModal(deviceId)
                  }}
                />
              );
            })}
          </MapContainer>
        </div>
      </WalteroCard>
    );
  };

  const renderGalleryView = () => (
    <div className="vision-monitor-shell">
      <div className="vision-monitor-header">
        <div>
          <WalteroTypography variant="h6" component="h3">
            Gallery
          </WalteroTypography>
          <WalteroTypography variant="body2" color="textSecondary">
            Latest frames from each sensor in a grid.
          </WalteroTypography>
        </div>
        <Chip label={`${devicesWithImages.length}/${devices.length} sensors with imagery`} size="small" color="secondary" />
      </div>
      <div className="vision-monitor-grid">
        {devices.map(device => {
          const cacheEntry = imageCache[device.id];
          const latestImage = cacheEntry?.latest;
          const timestamp = getTimestamp(latestImage);

          return (
            <WalteroCard key={device.id} className="vision-monitor-card">
              <div className="vision-monitor-image-wrapper" onClick={() => handleOpenModal(device.id)} role="button" tabIndex={0}>
                {latestImage ? (
                  <img
                    src={latestImage.url}
                    alt={`Latest capture for ${device.serialnumber || device.name}`}
                    className="vision-monitor-image"
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="vision-monitor-placeholder">No image</div>
                )}
              </div>
              <div className="vision-monitor-meta">
                <WalteroTypography variant="subtitle2">
                  {device.name || device.serialnumber || 'Sensor'}
                </WalteroTypography>
                <WalteroTypography variant="caption" color="textSecondary">
                  Serial: {device.serialnumber || '—'}
                </WalteroTypography>
                {latestImage ? (
                  <WalteroTypography variant="caption" color="textSecondary">
                    Captured {timestamp ? formatDateTime(timestamp) : 'Unknown time'}
                  </WalteroTypography>
                ) : (
                  <WalteroTypography variant="caption" color="textSecondary">
                    No capture in range
                  </WalteroTypography>
                )}
                <WalteroButton
                  size="small"
                  walteroVariant="outlined"
                  onClick={() => handleOpenModal(device.id)}
                  disabled={!latestImage}
                  sx={{ mt: 1 }}
                >
                  View Feed
                </WalteroButton>
              </div>
            </WalteroCard>
          );
        })}
      </div>
    </div>
  );

  const renderTimelineView = () => (
    <div className="vision-timeline-shell">
      <div className="vision-timeline-header">
        <div>
          <WalteroTypography variant="h6" component="h3">
            Timeline
          </WalteroTypography>
          <WalteroTypography variant="body2" color="textSecondary">
            Quickly scan when each sensor reported imagery.
          </WalteroTypography>
        </div>
        <Chip label={`${allPictures.length} images processed`} size="small" color="primary" />
      </div>
      <div className="vision-timeline-list">
        {devices.map(device => {
          const cacheEntry = imageCache[device.id];
          const pictures = cacheEntry?.pictures || [];
          const hasPictures = pictures.length > 0;
          const sortedTimelinePictures = sortPicturesByNewest(pictures);

          return (
            <WalteroCard key={device.id} className="vision-timeline-card">
              <div className="vision-timeline-device">
                <div>
                  <WalteroTypography variant="subtitle2">
                    {device.name || device.serialnumber || 'Sensor'}
                  </WalteroTypography>
                  <WalteroTypography variant="caption" color="textSecondary">
                    {device.serialnumber || '—'}
                  </WalteroTypography>
                </div>
                <Chip
                  label={hasPictures ? `${pictures.length} shot${pictures.length === 1 ? '' : 's'}` : 'No captures'}
                  size="small"
                  color={hasPictures ? 'primary' : 'default'}
                />
              </div>
              <div
                className="vision-timeline-track"
                role="list"
                onScroll={(event) => handleTimelineScroll(device.id, event)}
              >
                {hasPictures ? (
                  sortedTimelinePictures.map((picture, index) => {
                      const ts = getTimestamp(picture);
                      const label = ts ? formatTime(ts) : '—';
                      const detail = ts ? formatDateTime(ts) : 'Timestamp unavailable';
                      const canonicalIndex = (cacheEntry?.pictures || []).findIndex(original => {
                        const primaryKey = original.key || original.url || original.filename;
                        const comparisonKey = picture.key || picture.url || picture.filename;
                        return primaryKey && comparisonKey ? primaryKey === comparisonKey : original === picture;
                      });
                      const targetIndex = canonicalIndex >= 0 ? canonicalIndex : index;

                      const imageUrl = picture.url || picture.previewUrl || picture.thumbnailUrl || picture.imageUrl;

                      return (
                        <Tooltip key={picture.key || `${device.id}-${index}`} title={detail} arrow>
                          <button
                            type="button"
                            className="vision-timeline-dot"
                            onClick={() => handleOpenModal(device.id, targetIndex)}
                          >
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={detail}
                                className="vision-timeline-thumb"
                                onError={(event) => {
                                  event.currentTarget.style.visibility = 'hidden';
                                  event.currentTarget.nextElementSibling?.classList.add('vision-timeline-thumb-placeholder');
                                }}
                              />
                            ) : (
                              <div className="vision-timeline-thumb vision-timeline-thumb-placeholder" />
                            )}
                            <span className="vision-timeline-time">{label}</span>
                            <span className="vision-timeline-date">{ts ? ts.toISOString().split('T')[0] : '—'}</span>
                          </button>
                        </Tooltip>
                      );
                    })
                ) : (
                  <span className="vision-timeline-empty">No timeline entries in this window.</span>
                )}
              </div>
              {cacheEntry?.isFetchingMore && (
                <div className="vision-timeline-loader">Loading more…</div>
              )}
            </WalteroCard>
          );
        })}
      </div>
    </div>
  );

  if (!selectedOrgId) {
    return (
      <WalteroCard className="vision-empty-card">
        <WalteroTypography variant="body1" color="textSecondary">
          Select an organization to unlock Vision.
        </WalteroTypography>
      </WalteroCard>
    );
  }

  return (
    <div className="vision-tab-root">
      <div className="vision-summary-grid">
        <WalteroCard className="vision-summary-card">
          <WalteroTypography variant="subtitle2" color="textSecondary">
            Sensors processed
          </WalteroTypography>
          <WalteroTypography variant="h5">{devices.length}</WalteroTypography>
        </WalteroCard>
        <WalteroCard className="vision-summary-card">
          <WalteroTypography variant="subtitle2" color="textSecondary">
            Active feeds
          </WalteroTypography>
          <WalteroTypography variant="h5">{devicesWithImages.length}</WalteroTypography>
        </WalteroCard>
        <WalteroCard className="vision-summary-card">
          <WalteroTypography variant="subtitle2" color="textSecondary">
            Capture window
          </WalteroTypography>
          <WalteroTypography variant="body2">
            {captureWindow.first ? formatDateTime(captureWindow.first) : '—'}
          </WalteroTypography>
          <WalteroTypography variant="caption" color="textSecondary">
            {captureWindow.last && captureWindow.first && captureWindow.last.getTime() !== captureWindow.first.getTime()
              ? `Latest: ${formatDateTime(captureWindow.last)}`
              : ''}
          </WalteroTypography>
        </WalteroCard>
        <WalteroCard className="vision-summary-card">
          <WalteroTypography variant="subtitle2" color="textSecondary">
            Total images indexed
          </WalteroTypography>
          <WalteroTypography variant="h5">{allPictures.length}</WalteroTypography>
        </WalteroCard>
      </div>

      {deviceError && (
        <Alert severity="error">{deviceError}</Alert>
      )}

      {imageError && devicesWithImages.length === 0 && !imageLoading && (
        <Alert severity="warning">{imageError}</Alert>
      )}

      {(deviceLoading || imageLoading) && renderLoadingState()}

      {!deviceLoading && !imageLoading && devices.length === 0 && renderEmptyState()}

      {devices.length > 0 && (
        <>
          <div className="vision-mode-tabs">
            <WalteroTabs
              tabs={VIEW_MODES.map(mode => ({ id: mode.id, label: mode.label }))}
              value={activeMode}
              onChange={setActiveMode}
              ariaLabel="Vision view modes"
            />
            <WalteroTypography variant="body2" color="textSecondary">
              {VIEW_MODES.find(mode => mode.id === activeMode)?.description}
            </WalteroTypography>
          </div>

          <WalteroTabPanel value={activeMode} index="map">
            {renderMapView()}
          </WalteroTabPanel>
          <WalteroTabPanel value={activeMode} index="gallery">
            {renderGalleryView()}
          </WalteroTabPanel>
          <WalteroTabPanel value={activeMode} index="timeline">
            {renderTimelineView()}
          </WalteroTabPanel>
        </>
      )}

      <ImageModal
        isOpen={modalOpen}
        selectedImage={selectedImage}
        pictures={modalPictures}
        currentIndex={modalIndex}
        onClose={handleCloseModal}
        onNext={handleNextModalImage}
        onPrev={handlePrevModalImage}
        title="Sensor Feed"
      />
    </div>
  );
};

VisionTab.propTypes = {
  selectedOrgId: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  refreshKey: PropTypes.number
};

VisionTab.defaultProps = {
  selectedOrgId: '',
  startDate: null,
  endDate: null,
  refreshKey: 0
};

export default VisionTab;
